import { useState } from 'react'
import { useConfirmDelete } from '../hooks/useConfirmDelete'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { committentiApi } from '../services/api'
import { Committente } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { Plus, Search, Building2, Phone, Mail, Edit, Trash2, List, X, Save, Download, Upload } from 'lucide-react'
import toast from 'react-hot-toast'

interface VoceListino {
  id: number
  committente_id: number
  codice: string | null
  descrizione: string
  importo: number
  sconto_percentuale: number
  netto: number
  is_active: boolean
}

export default function Committenti() {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const [search, setSearch] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<Committente | null>(null)
  const [listinoFor, setListinoFor] = useState<Committente | null>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['committenti', search],
    queryFn: () => committentiApi.list({ search, per_page: 50 }).then(r => r.data),
  })

  const deleteMutation = useMutation({
    mutationFn: ({ id, force }: { id: number; force: boolean }) => committentiApi.delete(id, force),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['committenti'] })
      toast.success('Committente eliminato')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail ?? ''
      if (detail.includes('pratiche')) {
        confirmDelete(`${detail}\n\nConfermi eliminazione forzata (cancella anche pratiche e fatture collegate)?`, () => {
          const id = (deleteMutation.variables as any)?.id
          if (id) deleteMutation.mutate({ id, force: true })
        })
      } else {
        toast.error('Errore nell\'eliminazione')
      }
    },
  })

  const items: Committente[] = data?.items ?? []
  const { user } = useAuth()
  const isReadonly = user?.role === 'readonly'

  return (
    <div className="space-y-4">
      {ConfirmModal}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Committenti</h1>
          <p className="text-slate-500 text-sm">{data?.total ?? 0} committenti</p>
        </div>
        {!isReadonly && (
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={16} /> Nuovo Committente
          </button>
        )}
      </div>

      <div className="card p-4">
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input pl-9" placeholder="Cerca per ragione sociale, P.IVA..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-12 text-slate-400">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400">Nessun committente trovato</div>
        ) : items.map((c) => (
          <div key={c.id} className="card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Building2 size={18} className="text-blue-700" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 leading-tight">{c.ragione_sociale}</div>
                  <div className="text-xs text-slate-400">P.IVA: {c.piva || '—'}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setListinoFor(c)} className="btn-ghost px-2 py-1" title="Listino">
                  <List size={14} />
                </button>
                {!isReadonly && (
                  <>
                    <button onClick={() => setEditing(c)} className="btn-ghost px-2 py-1" title="Modifica">
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => {
                        confirmDelete(`Eliminare ${c.ragione_sociale}?`, () => deleteMutation.mutate({ id: c.id, force: false }))
                      }}
                      className="btn-ghost px-2 py-1 text-red-500 hover:text-red-700"
                      title="Elimina"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="space-y-1.5 text-xs text-slate-600">
              {c.telefono && <div className="flex items-center gap-1.5"><Phone size={11} className="text-slate-400" />{c.telefono}</div>}
              {c.email && <div className="flex items-center gap-1.5"><Mail size={11} className="text-slate-400" />{c.email}</div>}
              {c.sede_legale?.comune && (
                <div className="text-slate-500">{c.sede_legale.comune} ({c.sede_legale.provincia})</div>
              )}
            </div>
            {c.sla_config && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-1.5">SLA Configurati</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(c.sla_config).map(([k, v]: [string, unknown]) => {
                    const sla = v as { chiusura_h: number }
                    return (
                      <span key={k} className="badge bg-slate-100 text-slate-600">
                        {k}: {sla.chiusura_h}h
                      </span>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {(showCreate || editing) && (
        <CommittenteModal
          committente={editing}
          onClose={() => { setShowCreate(false); setEditing(null) }}
          onSuccess={() => {
            setShowCreate(false); setEditing(null)
            qc.invalidateQueries({ queryKey: ['committenti'] })
            toast.success(editing ? 'Committente aggiornato!' : 'Committente creato!')
          }}
        />
      )}

      {listinoFor && (
        <ListinoModal
          committente={listinoFor}
          onClose={() => setListinoFor(null)}
        />
      )}
    </div>
  )
}

function CommittenteModal({ committente, onClose, onSuccess }: {
  committente?: Committente | null; onClose: () => void; onSuccess: () => void
}) {
  const existingSla = (committente as any)?.sla_config || {}
  const [form, setForm] = useState({
    ragione_sociale: committente?.ragione_sociale ?? '',
    piva: committente?.piva ?? '',
    codice_sdi: committente?.codice_sdi ?? '',
    pec: committente?.pec ?? '',
    telefono: committente?.telefono ?? '',
    email: committente?.email ?? '',
    giorni_pagamento: 30,
  })
  const [slaForm, setSlaForm] = useState({
    critica: String(existingSla.critica ?? 4),
    urgente: String(existingSla.urgente ?? 8),
    alta:    String(existingSla.alta ?? 24),
    normale: String(existingSla.normale ?? 48),
  })
  const f = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const sf = (k: string, v: string) => setSlaForm(p => ({ ...p, [k]: v }))

  const mutation = useMutation({
    mutationFn: (data: typeof form) => {
      const payload = {
        ...data,
        sla_config: {
          critica: Number(slaForm.critica) || 4,
          urgente: Number(slaForm.urgente) || 8,
          alta:    Number(slaForm.alta) || 24,
          normale: Number(slaForm.normale) || 48,
        },
      }
      return committente ? committentiApi.update(committente.id, payload) : committentiApi.create(payload)
    },
    onSuccess,
    onError: () => toast.error('Errore nel salvataggio'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">{committente ? 'Modifica Committente' : 'Nuovo Committente'}</h2>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(form) }} className="p-6 space-y-4">
          <div><label className="label">Ragione Sociale *</label><input className="input" value={form.ragione_sociale} onChange={(e) => f('ragione_sociale', e.target.value)} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">P.IVA</label><input className="input" value={form.piva} onChange={(e) => f('piva', e.target.value)} /></div>
            <div><label className="label">Codice SDI</label><input className="input" value={form.codice_sdi} onChange={(e) => f('codice_sdi', e.target.value)} /></div>
            <div><label className="label">PEC</label><input type="email" className="input" value={form.pec} onChange={(e) => f('pec', e.target.value)} /></div>
            <div><label className="label">Telefono</label><input className="input" value={form.telefono} onChange={(e) => f('telefono', e.target.value)} /></div>
            <div className="col-span-2"><label className="label">Email</label><input type="email" className="input" value={form.email} onChange={(e) => f('email', e.target.value)} /></div>
          </div>
          {/* SLA-3: Configurazione SLA per priorità */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-700">Configurazione SLA</p>
              <p className="text-xs text-slate-400 mt-0.5">Ore massime per chiudere un WO in base alla priorità</p>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {([
                ['critica', 'Critica', '#ef4444'],
                ['urgente', 'Urgente', '#f97316'],
                ['alta',    'Alta',    '#f59e0b'],
                ['normale', 'Normale', '#6b7280'],
              ] as [keyof typeof slaForm, string, string][]).map(([key, label, color]) => (
                <div key={key}>
                  <label className="label flex items-center gap-1">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                    {label}
                  </label>
                  <div className="flex items-center gap-1">
                    <input
                      type="number" min={1} max={720}
                      className="input text-center"
                      value={slaForm[key]}
                      onChange={e => sf(key, e.target.value)}
                    />
                    <span className="text-xs text-slate-400 shrink-0">h</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Annulla</button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary flex-1">
              {mutation.isPending ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ListinoModal({ committente, onClose }: {
  committente: Committente; onClose: () => void
}) {
  const { confirmDelete, ConfirmModal } = useConfirmDelete()
  const qc = useQueryClient()
  const [newVoce, setNewVoce] = useState({ codice: '', descrizione: '', importo: 0, sconto_percentuale: 0, netto: 0 })
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ codice: '', descrizione: '', importo: 0, sconto_percentuale: 0, netto: 0 })

  const { data: voci, isLoading } = useQuery({
    queryKey: ['listino', committente.id],
    queryFn: () => committentiApi.getListino(committente.id).then(r => r.data as VoceListino[]),
  })

  const calcNetto = (importo: number, sconto: number) => Math.round(importo * (1 - sconto / 100) * 100) / 100

  const addMutation = useMutation({
    mutationFn: (data: typeof newVoce) => committentiApi.addVoceListino(committente.id, {
      ...data,
      netto: data.netto || calcNetto(data.importo, data.sconto_percentuale),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listino', committente.id] })
      setNewVoce({ codice: '', descrizione: '', importo: 0, sconto_percentuale: 0, netto: 0 })
      toast.success('Voce aggiunta')
    },
    onError: () => toast.error('Errore'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: typeof editForm }) => committentiApi.updateVoceListino(id, {
      ...data,
      netto: data.netto || calcNetto(data.importo, data.sconto_percentuale),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listino', committente.id] })
      setEditingId(null)
      toast.success('Voce aggiornata')
    },
    onError: () => toast.error('Errore'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => committentiApi.deleteVoceListino(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listino', committente.id] })
      toast.success('Voce eliminata')
    },
    onError: () => toast.error('Errore'),
  })

  const startEdit = (v: VoceListino) => {
    setEditingId(v.id)
    setEditForm({ codice: v.codice || '', descrizione: v.descrizione, importo: v.importo, sconto_percentuale: v.sconto_percentuale, netto: v.netto })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Listino — {committente.ragione_sociale}</h2>
            <p className="text-sm text-slate-500">{voci?.length ?? 0} voci</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                try {
                  const res = await committentiApi.exportListino(committente.id)
                  const url = URL.createObjectURL(new Blob([res.data]))
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `listino_${committente.ragione_sociale.replace(/ /g, '_')}.xlsx`
                  a.click()
                  URL.revokeObjectURL(url)
                } catch { toast.error('Errore export') }
              }}
              className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1"
            >
              <Download size={13} /> Export XLSX
            </button>
            <label className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 cursor-pointer">
              <Upload size={13} /> Import XLSX
              <input type="file" accept=".xlsx" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0]
                if (!file) return
                try {
                  const res = await committentiApi.importListino(committente.id, file)
                  qc.invalidateQueries({ queryKey: ['listino', committente.id] })
                  toast.success(`Importate ${res.data.imported} voci`)
                } catch { toast.error('Errore import') }
                e.target.value = ''
              }} />
            </label>
            <button onClick={onClose} className="btn-ghost px-2 py-1"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50/50">
                <th className="text-left px-3 py-2 font-semibold text-slate-600 w-24">Codice</th>
                <th className="text-left px-3 py-2 font-semibold text-slate-600">Descrizione</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600 w-24">Importo</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600 w-20">Sconto %</th>
                <th className="text-right px-3 py-2 font-semibold text-slate-600 w-24">Netto</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600 w-24">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr><td colSpan={6} className="py-8 text-center text-slate-400">Caricamento...</td></tr>
              ) : voci?.map((v) => (
                editingId === v.id ? (
                  <tr key={v.id} className="bg-blue-50/50">
                    <td className="px-3 py-2"><input className="input text-xs" value={editForm.codice} onChange={(e) => setEditForm(p => ({ ...p, codice: e.target.value }))} /></td>
                    <td className="px-3 py-2"><input className="input text-xs" value={editForm.descrizione} onChange={(e) => setEditForm(p => ({ ...p, descrizione: e.target.value }))} /></td>
                    <td className="px-3 py-2"><input type="number" step="0.01" className="input text-xs text-right" value={editForm.importo} onChange={(e) => setEditForm(p => ({ ...p, importo: parseFloat(e.target.value) || 0 }))} /></td>
                    <td className="px-3 py-2"><input type="number" step="0.1" className="input text-xs text-right" value={editForm.sconto_percentuale} onChange={(e) => setEditForm(p => ({ ...p, sconto_percentuale: parseFloat(e.target.value) || 0 }))} /></td>
                    <td className="px-3 py-2"><input type="number" step="0.01" className="input text-xs text-right" value={editForm.netto} onChange={(e) => setEditForm(p => ({ ...p, netto: parseFloat(e.target.value) || 0 }))} /></td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => updateMutation.mutate({ id: v.id, data: editForm })} className="btn-ghost px-1.5 py-1 text-green-600"><Save size={13} /></button>
                        <button onClick={() => setEditingId(null)} className="btn-ghost px-1.5 py-1"><X size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={v.id} className="table-row-hover">
                    <td className="px-3 py-2 text-slate-500 font-mono text-xs">{v.codice || '—'}</td>
                    <td className="px-3 py-2 text-slate-800">{v.descrizione}</td>
                    <td className="px-3 py-2 text-right font-mono">{v.importo.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right">{v.sconto_percentuale}%</td>
                    <td className="px-3 py-2 text-right font-semibold font-mono">{v.netto.toFixed(2)}</td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <button onClick={() => startEdit(v)} className="btn-ghost px-1.5 py-1"><Edit size={13} /></button>
                        <button onClick={() => confirmDelete('Eliminare questa voce?', () => deleteMutation.mutate(v.id))} className="btn-ghost px-1.5 py-1 text-red-500"><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                )
              ))}

              {/* New row */}
              <tr className="bg-green-50/30">
                <td className="px-3 py-2"><input className="input text-xs" placeholder="Codice" value={newVoce.codice} onChange={(e) => setNewVoce(p => ({ ...p, codice: e.target.value }))} /></td>
                <td className="px-3 py-2"><input className="input text-xs" placeholder="Descrizione *" value={newVoce.descrizione} onChange={(e) => setNewVoce(p => ({ ...p, descrizione: e.target.value }))} /></td>
                <td className="px-3 py-2"><input type="number" step="0.01" className="input text-xs text-right" placeholder="0.00" value={newVoce.importo || ''} onChange={(e) => setNewVoce(p => ({ ...p, importo: parseFloat(e.target.value) || 0 }))} /></td>
                <td className="px-3 py-2"><input type="number" step="0.1" className="input text-xs text-right" placeholder="0" value={newVoce.sconto_percentuale || ''} onChange={(e) => setNewVoce(p => ({ ...p, sconto_percentuale: parseFloat(e.target.value) || 0 }))} /></td>
                <td className="px-3 py-2"><input type="number" step="0.01" className="input text-xs text-right" placeholder="auto" value={newVoce.netto || ''} onChange={(e) => setNewVoce(p => ({ ...p, netto: parseFloat(e.target.value) || 0 }))} /></td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() => { if (newVoce.descrizione) addMutation.mutate(newVoce) }}
                    disabled={!newVoce.descrizione || addMutation.isPending}
                    className="btn-primary px-3 py-1 text-xs"
                  >
                    <Plus size={13} /> Aggiungi
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="btn-secondary">Chiudi</button>
        </div>
      </div>
      {ConfirmModal}
    </div>
  )
}
